import { useMemo, useState } from 'react'

import { QuizOutcome, QuizStatus, QuizzesResultsType } from 'app/types'
import { useQuery } from 'hooks'

import { countCorrectAnswers } from './quiz-helpers'

export const QUIZ_ATTEMPTS_ENDPOINT = '/ca/v1/quiz-attempts'

const createMountScope = () => `${Date.now()}-${Math.random()}`

/**
 * Each mount caches under its own key, so a playlist returning to a guide fetches the attempts passed
 * meanwhile. Nothing refetches after a submit: a quiz passed mid-view must stay in the list so Rewatch
 * can still rewind to it.
 */
export const useQuizStatuses = (playbookId: string, enabled = true) => {
    const url = enabled && playbookId ? `${QUIZ_ATTEMPTS_ENDPOINT}?playbookId=${playbookId}` : ''
    const [mountScope] = useState(createMountScope)

    const { data, error } = useQuery<QuizzesResultsType>(url, { method: 'GET', cacheScope: mountScope })

    const statusByQuizId = useMemo(
        () =>
            Object.fromEntries((data?.quizzes ?? []).map(quiz => [quiz.quizId, quiz.status])) as Record<
                string,
                QuizStatus
            >,
        [data],
    )

    const outcomeByQuizId = useMemo(
        () =>
            Object.fromEntries(
                (data?.quizzes ?? []).map(({ quizId, latestAttempt }): [string, QuizOutcome] => [
                    quizId,
                    {
                        correctCount: countCorrectAnswers(latestAttempt.answeredQuestions),
                        isPassed: latestAttempt.isPassed,
                    },
                ]),
            ),
        [data],
    )

    const isLoading = Boolean(url) && !data && !error

    return { statusByQuizId, outcomeByQuizId, isLoading }
}
